<template>
  <div ref="rootEl" class="tw-flex tw-gap-4">
    <template v-if="!noRemarks">
      <template v-if="!exportInProgress">
        <SfButtonDs
          v-if="isCompactMode"
          v-tooltip="copy.exports.download"
          icon-only
          icon-before="download"
          variant="tertiary"
          data-heap="talking-points-export-current"
          @click="generateExportHandler"
        />
        <SfButtonDs
          v-else
          variant="tertiary"
          data-heap="talking-points-export-current"
          @click="generateExportHandler"
        >
          <i class="fa fa-download" />
          <span>{{ copy.exports.download }}</span>
        </SfButtonDs>
      </template>
      <div v-else class="tw-flex tw-items-center tw-gap-2 tw-p-1 tw-pl-2">
        <span class="sf-spinner" />
        <span v-if="!isCompactMode">{{ copy.exports.generating }}</span>
      </div>
      <div
        v-if="!isSidebarCompact"
        class="tw-w-px tw-my-1 tw-self-stretch tw-bg-sf-neutral-lighter-1"
      ></div>
    </template>
    <div
      v-if="isSidebarCompact && !noRemarks"
      class="tw-w-px tw-my-1 tw-self-stretch tw-bg-sf-neutral-lighter-1"
    ></div>

    <template v-if="!isSidebarCompact">
      <SfButtonDs
        v-if="isCompactMode"
        v-tooltip="copy.externalNoteButton"
        icon-only
        icon-before="upload"
        variant="secondary"
        :disabled="isProcessingLoading"
        @click="openExternalNoteModal"
      />
      <SfButtonDs
        v-else
        variant="secondary"
        :disabled="isProcessingLoading"
        @click="openExternalNoteModal"
      >
        <i class="fa-solid fa-upload tw-text-[14px]"></i>
        {{ copy.externalNoteButton }}
        <template v-if="documents.length">({{ documents.length }})</template>
      </SfButtonDs>
    </template>
    <span
      v-if="!isSidebarCompact"
      :title="copy.processMeeting.disabledTooltip"
      data-toggle="tooltip"
      data-placement="bottom"
    >
      <SfButtonDs
        variant="secondary"
        :disabled="isProcessingDisabled || pageData.companyHasPendingProposal"
        @click="confirmProcessMeeting"
      >
        <span class="tw-flex tw-items-center tw-gap-1">
          <span v-if="isProcessingLoading" class="sf-spinner" />
          {{ copy.processMeeting.button }}
        </span>
      </SfButtonDs>
    </span>
    <div
      v-if="!isSidebarCompact"
      class="tw-w-px tw-my-1 tw-self-stretch tw-bg-sf-neutral-lighter-1"
    ></div>
    <SfButtonDs
      v-if="isSidebarCompact"
      v-tooltip="copy.present"
      icon-only
      icon-before="display"
      variant="secondary"
      data-testid="present-toggle"
      @click="switchToPresentationMode"
    />
    <SfButtonDs
      v-else
      variant="secondary"
      data-testid="present-toggle"
      @click="switchToPresentationMode"
    >
      <span class="tw-flex tw-items-center tw-gap-2">
        <span>{{ copy.present }}</span>
        <SfToggle :model-value="isPresenting" :icons="false" @click.prevent />
      </span>
    </SfButtonDs>

    <NewRemarkButton
      owner-type="ClientMeeting"
      :owner-id="pageData.clientMeetingId"
      :hide-period-selector="true"
      :selected-tag-ids="selectedTagIds"
      :is-client-meeting-remark="true"
      modal-size="l"
    />

    <SfButtonDs
      v-if="pageData.suggestionsEnabled && !openedSidebar"
      variant="secondary"
      class="tw-min-w-9 tw-h-9"
      @click="openSidebar"
    >
      <span class="tw-flex tw-items-center tw-gap-2">
        <i class="fa fa-wand-magic-sparkles" />
        <span
          v-if="loadingSuggestions"
          class="sf-spinner tw-border-sf-ai-light-2"
        />
        <SfIndicatorCount v-else ai data-testid="suggested-talking-point-count">
          {{ suggestionCount }}
        </SfIndicatorCount>
      </span>
    </SfButtonDs>
    <ConfirmProcessMeetingModal />
  </div>
</template>

<script setup lang="ts">
import type { Ref } from 'vue'
import { ref, computed, onMounted, onUpdated, inject } from 'vue'
import { storeToRefs } from 'pinia'

import type { PreparePageData } from '@app/application/client_meetings/types'

import { isUsingNewLayout } from '@app/adapters/silverfin'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import SfToggle from '@app/shared/vue/components/sf-toggle.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import NewRemarkButton from '@app/shared/vue/components/new-remark/new-remark-button.vue'
import ConfirmProcessMeetingModal from '@app/application/client_meetings/screens/prepare/components/confirm-process-meeting-modal.vue'
import { SfIndicatorCount } from '@app/shared/vue/components/sf-indicator'
import { useSuggestedTalkingPointsStore } from '@app/application/client_meetings/screens/prepare/stores/suggested-talking-points'
import useOrderedRemarks from '@app/application/client_meetings/screens/prepare/composables/use-ordered-remarks'
import { useTalkingPointTag } from '@app/application/client_meetings/composables/use-talking-point-tag'
import { useDrawer } from '@app/shared/pinia/drawer'
import useAutoCompact from '@app/shared/vue/composables/use-auto-compact'
import useExport from '@app/application/client_meetings/screens/composables/use-export'
import { RightDrawerContent } from '@app/shared/types/drawer'
import { setSafeJSONValue } from '@app/utils/local-storage'
import { useExternalNoteStore } from '@app/application/client_meetings/screens/prepare/stores/external-note'
import * as PrepareApi from '@app/application/client_meetings/api/prepare'
import { SfLog } from '@app/adapters/logger'

const pageData = inject('pageData') as PreparePageData

const I18n = useI18n()
const copy = {
  exports: {
    download: I18n.t('client_meetings.exports.download'),
    generating: I18n.t('client_meetings.exports.generating'),
  },
  processMeeting: {
    button: I18n.t('client_meetings.prepare.process_meeting_button'),
    disabledTooltip: pageData.companyHasPendingProposal
      ? I18n.t('client_meetings.prepare.disabled_tooltip')
      : '',
  },
  present: I18n.t('client_meetings.prepare.present'),
  externalNoteButton: I18n.t(
    'client_meetings.prepare.external_note_button.label'
  ),
}

const externalNoteStore = useExternalNoteStore()
const { documents } = storeToRefs(externalNoteStore)

const drawerStore = useDrawer()
const { open, rightContent } = storeToRefs(drawerStore)

const isProcessingLoading: Ref<boolean> = ref(false)

function openSidebar() {
  setSafeJSONValue('showMeetingPreparationSidebar', true)
  drawerStore.openRightDrawer(RightDrawerContent.TPSuggestions, true)
}

const openedSidebar = computed(() =>
  !usingNewLayout
    ? open.value.content === RightDrawerContent.TPSuggestions
    : rightContent.value === RightDrawerContent.TPSuggestions
)

const { talkingPointTagIds: selectedTagIds } = useTalkingPointTag()

const { unorderedRemarks, joinUnordered, remarksCount } = useOrderedRemarks()

const noRemarks = computed(() => remarksCount.value === 0)

function openExternalNoteModal() {
  window.modalCitizen.openModalPortal('meeting-external-note-modal')
}

function confirmProcessMeeting() {
  window.modalCitizen.openModalPortal('confirm-process-meeting-modal', {
    onConfirm: processMeeting,
  })
}

async function processMeeting() {
  isProcessingLoading.value = true

  try {
    // We also use this to make sure that the order was saved before processing
    await joinUnordered()
    await PrepareApi.process()

    visitPastMeetings()
  } catch (error) {
    SfLog.error('Failed to start processing the meeting', { error })
    alert(I18n.t('common.error.try_again'))
  }

  isProcessingLoading.value = false
}

const { exportInProgress, generateExport } = useExport({
  meetingId: pageData.clientMeetingId,
})

async function generateExportHandler() {
  if (unorderedRemarks.value.length) {
    await joinUnordered()
  }

  const response = await generateExport()
  if (response && response.url) {
    window.open(response.url, '_blank')
  }
}

const isPresenting = ref(false)

async function switchToPresentationMode() {
  isPresenting.value = true
  try {
    if (unorderedRemarks.value.length) {
      await joinUnordered()
    }
    setSafeJSONValue('presentationComments', [])
    await PrepareApi.enablePresentationMode(remarksCount.value)
  } catch (error) {
    SfLog.error('Failed to enable presentation mode', { error })
    isPresenting.value = false
    return
  }

  window.Turbolinks.visit(
    window.Silverfin.Routes.client_meetings_present_path(
      window.Silverfin.currentFirmId,
      window.Silverfin.currentCompanyId
    )
  )
}

const suggestedTalkingPointsStore = useSuggestedTalkingPointsStore()

const { suggestionCount, loadingSuggestions } = storeToRefs(
  suggestedTalkingPointsStore
)

const isProcessingDisabled = computed(
  () => noRemarks.value || isProcessingLoading.value
)

const usingNewLayout = isUsingNewLayout()

// Collapse buttons to icon-only when the subheader runs out of horizontal
// space (window size, left drawer and suggestions sidepane all affect it)
const rootEl = ref<HTMLElement | null>(null)
const isCompactMode = useAutoCompact(rootEl)
const isSidebarCompact = computed(
  () => isCompactMode.value && openedSidebar.value
)

function initTooltips() {
  window.dispatchEvent(new CustomEvent('tooltipsRendered'))
}

function visitPastMeetings() {
  window.Turbolinks.visit(
    window.Silverfin.Routes.client_meetings_past_path(
      window.Silverfin.currentFirmId,
      window.Silverfin.currentCompanyId,
      { anchor: 'meeting-' + pageData.clientMeetingId }
    )
  )
}

onMounted(() => {
  initTooltips()
})

onUpdated(() => {
  initTooltips()
})
</script>
